import { getAuth, onAuthStateChanged, onIdTokenChanged, signOut } from 'firebase/auth'
import { getFirestore, doc, getDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { functions, environment as firebaseClientEnvironment } from '@/firebase/init'
import { markSignedOut, recentlySignedOut } from '@/services/signOutMarker'

const LOGGED_OUT = Object.freeze({ authUser: null, contactId: null, claims: {} })

// Every leg of the bootstrap gates the router and the App.vue overlay, so each
// one needs a ceiling: a single hung request would otherwise leave the whole app
// sitting on a spinner with nothing to retry.
const SYNC_CLAIM_TIMEOUT_MS = 12000
const TOKEN_FENCE_TIMEOUT_MS = 5000
const CANARY_TIMEOUT_MS = 10000
const BOOTSTRAP_TIMEOUT_MS = 25000
const READY_MATCH_TIMEOUT_MS = 15000

function withTimeout(promise, ms, label) {
  let timer = null
  const guard = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
  })
  return Promise.race([promise, guard]).finally(() => clearTimeout(timer))
}

/** Same intent as App.vue shouldLogIdTokenForDebug — enable with ?debugFirestore=1 or sessionStorage.renedian_debug_firestore = '1'. */
export function shouldDebugFirestoreAuth() {
  if (typeof window === 'undefined') {
    return firebaseClientEnvironment === 'development'
  }
  if (firebaseClientEnvironment === 'development') return true
  if (process.env.NODE_ENV !== 'production') return true
  try {
    if (sessionStorage.getItem('renedian_debug_firestore') === '1') return true
  } catch (e) { /* private mode */ }
  try {
    return new URLSearchParams(window.location.search).get('debugFirestore') === '1'
  } catch (e) {
    return false
  }
}

function dbg(tag, payload) {
  if (!shouldDebugFirestoreAuth()) return
  console.log(`[authBootstrap] ${tag}`, payload)
}

let latest = { authUser: null, contactId: null, claims: {} }
const subscribers = new Set()

let generation = 0
let latestWorkPromise = null

let firstLatch = null
let firstLatchResolve = null

let listenerStarted = false

function notifyAll(payload) {
  latest = payload
  for (const cb of subscribers) {
    try {
      cb(payload)
    } catch (e) {
      console.error('[authBootstrap] subscriber error', e)
    }
  }
}

/**
 * Subscribe before getIdToken(true), skip the immediate onIdTokenChanged delivery,
 * then await the next delivery so Firestore's internal credentials listener has
 * processed the same token update.
 */
function waitForNextIdTokenAfterSubscribe(auth, uid) {
  return new Promise((resolve) => {
    let first = true
    let unsub = null
    let timer = null

    const finish = () => {
      if (timer) clearTimeout(timer)
      if (typeof unsub === 'function') {
        try { unsub() } catch (e) { /* already torn down */ }
      }
      resolve()
    }

    unsub = onIdTokenChanged(auth, (user) => {
      if (!user || user.uid !== uid) return
      if (first) {
        first = false
        return
      }
      finish()
    })

    // A forced refresh re-fires this listener within milliseconds. When it
    // doesn't (throttled refresh, offline), carry on with the token we have
    // instead of stalling every gated route behind a promise that never settles.
    timer = setTimeout(() => {
      dbg('waitForNextIdToken:timeout', { uid })
      finish()
    }, TOKEN_FENCE_TIMEOUT_MS)
  })
}

/** Resolves on the next published bootstrap payload (or when `ms` elapses). */
function waitForNextPayload(ms) {
  return new Promise((resolve) => {
    let timer = null
    const done = () => {
      if (timer) clearTimeout(timer)
      subscribers.delete(done)
      resolve()
    }
    subscribers.add(done)
    timer = setTimeout(done, ms)
  })
}

async function runBootstrapForUser(authUser) {
  if (!authUser) {
    dbg('runBootstrapForUser', { authUser: null })
    return { authUser: null, contactId: null, claims: {} }
  }

  const auth = getAuth()
  dbg('runBootstrapForUser:start', { uid: authUser.uid, email: authUser.email })

  const fn = httpsCallable(functions, 'syncContactClaim')
  try {
    await withTimeout(fn({}), SYNC_CLAIM_TIMEOUT_MS, 'syncContactClaim')
  } catch (e) {
    // A failed claim sync only costs us the contactId. Employee access and
    // permissions are keyed off the uid, so the session is still usable and
    // aborting here would strand the user on a spinner.
    console.warn('[authBootstrap] syncContactClaim failed:', e?.code || e?.message || e)
  }
  dbg('runBootstrapForUser:afterSyncContactClaim', { uid: authUser.uid })

  const fence1 = waitForNextIdTokenAfterSubscribe(auth, authUser.uid)
  await authUser.getIdToken(true).catch((e) => {
    console.warn('[authBootstrap] token refresh failed:', e?.code || e?.message || e)
  })
  await fence1
  dbg('runBootstrapForUser:afterTokenFence1', { uid: authUser.uid })

  const tokenResult = await authUser.getIdTokenResult()
  const claims = tokenResult.claims || {}
  const contactId = claims.contactId || null
  dbg('runBootstrapForUser:tokenClaims', {
    uid: authUser.uid,
    contactId,
    claimKeys: Object.keys(claims),
    expirationTime: tokenResult.expirationTime,
  })

  const db = getFirestore()
  const canaryRef = doc(db, 'permissions', authUser.uid)

  const runCanary = () => withTimeout(getDoc(canaryRef), CANARY_TIMEOUT_MS, 'permissions canary')

  dbg('runBootstrapForUser:beforeCanary', { path: canaryRef.path })
  try {
    const canarySnap = await runCanary()
    dbg('runBootstrapForUser:canaryOk', {
      path: canaryRef.path,
      exists: canarySnap.exists(),
    })
  } catch (e) {
    dbg('runBootstrapForUser:canaryError', { code: e?.code, message: e?.message })
    if (e?.code === 'permission-denied') {
      const fence2 = waitForNextIdTokenAfterSubscribe(auth, authUser.uid)
      await authUser.getIdToken(true)
      await fence2
      dbg('runBootstrapForUser:afterTokenFence2(retry)', { uid: authUser.uid })
      const canarySnap2 = await runCanary()
      dbg('runBootstrapForUser:canaryOkAfterRetry', {
        path: canaryRef.path,
        exists: canarySnap2.exists(),
      })
    } else {
      throw e
    }
  }

  dbg('runBootstrapForUser:done', { uid: authUser.uid, contactId })
  return { authUser, contactId, claims }
}

function scheduleBootstrap(authUser) {
  const gen = ++generation
  dbg('scheduleBootstrap', {
    generation: gen,
    uid: authUser?.uid || null,
    hasUser: !!authUser,
  })
  latestWorkPromise = withTimeout(runBootstrapForUser(authUser), BOOTSTRAP_TIMEOUT_MS, 'authBootstrap')
    .then((payload) => {
      if (gen !== generation) return latest
      notifyAll(payload)
      return payload
    })
    .catch((err) => {
      console.error('[authBootstrap] bootstrap failed', err)
      const fallback = authUser
        ? { authUser: authUser, contactId: null, claims: {} }
        : LOGGED_OUT
      if (gen !== generation) return latest
      notifyAll(fallback)
      return fallback
    })
    .finally(() => {
      if (firstLatchResolve) {
        firstLatchResolve()
        firstLatchResolve = null
      }
    })

  return latestWorkPromise
}

function ensureAuthListener() {
  if (listenerStarted) return
  listenerStarted = true

  firstLatch = new Promise((r) => {
    firstLatchResolve = r
  })

  const auth = getAuth()
  onAuthStateChanged(auth, (user) => {
    // A user arriving within seconds of an explicit logout is the old session
    // handed back, not a sign-in: another tab still holding it refreshed its
    // token, which re-persisted the user to IndexedDB, and this tab's poll
    // picked it up (_onStorageEvent). Sign out again here, before any bootstrap
    // work refreshes the token and writes it back for the other tab in turn.
    // The mark is renewed so the window slides while the echo continues.
    if (user && recentlySignedOut()) {
      console.warn('[authBootstrap] session returned after sign-out; signing out again', { uid: user.uid })
      markSignedOut()
      signOut(auth).catch((e) => console.error('[authBootstrap] repeat sign-out failed:', e))
      return
    }
    scheduleBootstrap(user)
  })
}

/**
 * Resolves after the first auth state is known and bootstrap (sync + token fence +
 * Firestore canary) has finished for that state. Later calls await the latest
 * in-flight / completed bootstrap work.
 *
 * Awaits `auth.authStateReady()` first so IndexedDB persistence restore completes
 * before attaching `onAuthStateChanged` or reading `currentUser`.
 */
export function whenAuthBootstrapReady() {
  const auth = getAuth()
  return auth.authStateReady().then(async () => {
    dbg('whenAuthBootstrapReady:authStateReady', {
      currentUserUid: auth.currentUser?.uid || null,
    })
    ensureAuthListener()
    if (firstLatch) await firstLatch

    // signInWithPopup resolves before onAuthStateChanged has scheduled the next
    // bootstrap, so `latest` can still describe the signed-out session. Resolving
    // on that stale payload lets the router push the admin console before App.vue
    // has a user, which bounces the user back to the login page. Wait until the
    // published payload names the same user Firebase is reporting.
    const deadline = Date.now() + READY_MATCH_TIMEOUT_MS
    for (;;) {
      await (latestWorkPromise || Promise.resolve(latest))

      const liveUid = auth.currentUser?.uid || null
      const publishedUid = latest?.authUser?.uid || null
      if (liveUid === publishedUid) break

      const remaining = deadline - Date.now()
      if (remaining <= 0) {
        dbg('whenAuthBootstrapReady:uidMismatchTimeout', { liveUid, publishedUid })
        break
      }
      await waitForNextPayload(remaining)
    }

    dbg('whenAuthBootstrapReady:resolved', {
      uid: latest?.authUser?.uid || null,
      contactIdFromBootstrap: latest?.contactId || null,
    })
    return latest
  })
}

export function subscribeAuthBootstrap(callback) {
  if (typeof callback !== 'function') return () => {}
  subscribers.add(callback)
  try {
    callback(latest)
  } catch (e) {
    console.error('[authBootstrap] initial subscriber', e)
  }
  return () => subscribers.delete(callback)
}

/** Re-run sync + fence for the current Firebase user (e.g. after manual claim changes). */
export function refreshAuthBootstrap() {
  const auth = getAuth()
  return auth.authStateReady().then(() => {
    ensureAuthListener()
    return scheduleBootstrap(auth.currentUser)
  })
}
